import { Command } from 'commander'
import { execa } from 'execa'
import fs from 'fs-extra'
import path from 'path'
import prompts from 'prompts'
import z from 'zod'

import { preFlightInit } from '@preflights/preFlightInit'
import { txt } from '@utils/colors'
import { installDependencies } from '@utils/dependencyInstaller'
import { handleError } from '@utils/handleError'
import { logger } from '@utils/logger'
import { spinner } from '@utils/spinner'
import { GLOBAL_CSS_V4, TAILWIND_CONFIG_WITH_VARIABLES, UTILS } from '@utils/templates'

//List of dependencies for the project
const PROJECT_DEPENDENCIES = [
  'tailwindcss-animate',
  'class-variance-authority',
  'clsx',
  'tailwind-merge',
  'lucide-react',
]

export const initOptionsSchema = z.object({
  cwd: z.string(),
  yes: z.boolean(),
  defaultConfigurations: z.boolean(),
})

export type InitOptions = z.infer<typeof initOptionsSchema>

export const init = new Command()
  .name('init')
  .description('initializes your project with required dependencies')
  .option('-y,--yes', 'skip confirmation prompts', false)
  .option('-d,--defaultConfigurations', 'use default configurations', false)
  .option('-c,--cwd <cwd>', 'current working directory', process.cwd())
  .action(async (opts) => {
    try {
      const options = initOptionsSchema.parse(opts)

      await executeInit(options)

      logger.break()
      logger.info(`${txt.success('Success!')} Project initialization completed. You may now add components.`)
      logger.break()
    } catch (error) {
      handleError(error)
    }
  })

/**
 * Creates a global CSS file in the app directory.
 * @param projectDir - The root directory of the project.
 * @param hasSrcDir - Whether the project has a src directory or not.
 */
export const createGlobalCssFile = async (projectDir: string, hasSrcDir: boolean) => {
  try {
    const appDir = hasSrcDir ? path.join(projectDir, 'src', 'app') : path.join(projectDir, 'app')

    if (!fs.existsSync(appDir)) {
      throw new Error(`Could not find 'app' directory at the expected path: ${appDir}`)
    }

    const cssFile = path.join(appDir, 'globals.css')

    await fs.writeFile(cssFile, GLOBAL_CSS_V4, 'utf-8')
  } catch (error) {
    throw new Error(`Failed to create 'globals.css'${error.message}`)
  }
}

/**
 * Creates a utility file in the lib directory at the root directory.
 * @param projectDir - The root directory of the project.
 * @param hasSrcDir - Whether the project has a src directory or not.
 */
export const createUtils = async (projectDir: string, hasSrcDir: boolean) => {
  try {
    const baseDir = hasSrcDir ? path.join(projectDir, 'src') : projectDir

    const utilsDir = path.join(baseDir, 'lib')

    await fs.ensureDir(utilsDir)

    const utilsFile = path.join(utilsDir, 'utils.ts')

    await fs.writeFile(utilsFile, UTILS, 'utf8')
  } catch (error) {
    throw new Error(`Failed to create utils.ts: ${error}`)
  }
}

/**
 * Updates the Tailwind configuration file in the project directory.
 * @param projectPath - The root directory of the project.
 */
export const updateTailwindConfig = async (projectPath: string) => {
  try {
    await fs.ensureDir(projectPath)
    const filePath = path.join(projectPath, 'tailwind.config.ts')
    await fs.writeFile(filePath, TAILWIND_CONFIG_WITH_VARIABLES, 'utf-8')
  } catch (error) {
    throw new Error(`Failed to update tailwind.config.ts: ${error}`)
  }
}

/**
 * Initializes a project by:
 * 1. Running pre-flight checks.
 * 2. Creating a new project if no package.json exists.
 * 3. Writing a components.json file.
 * 4. Configuring the project (utils, Tailwind, global CSS).
 * 5. Installing required dependencies.
 *
 * @param {InitOptions} options - Includes `cwd` (working directory) and `yes` (skip prompts).
 */
const executeInit = async (options: InitOptions) => {
  const preflight = await preFlightInit(options)

  if (!preflight.projectInfo) {
    const confirmation = await prompts({
      type: 'confirm',
      name: 'confirmNewProject',
      message: `The path ${txt.bold(txt.info(options.cwd))} does't contain a package.json file. Would you like to create a new ${txt.bold(txt.info('Next.js'))} project?`,
      initial: false,
    })
    if (!confirmation.confirmNewProject) {
      process.exit()
    }
    const { projectPath } = await createNewProject({ cwd: options.cwd })
    options.cwd = projectPath
    return
  }

  const { hasSrcDir } = preflight.projectInfo

  //Write components.json file
  const componentSpinner = spinner('Writing components.json file').start()
  const targetPath = path.resolve(options.cwd, 'components.json')
  await fs.writeFile(targetPath, JSON.stringify({ hasSrcDir: hasSrcDir }, null, 2), 'utf8')
  componentSpinner.succeed()

  // Configure project
  const configurationSpinner = spinner('Setting up project configuration').start()
  await createUtils(options.cwd, hasSrcDir)
  // await updateTailwindConfig(options.cwd)
  await createGlobalCssFile(options.cwd, hasSrcDir)
  configurationSpinner.succeed('Project configuration completed')

  //Install the required project dependencies
  await installDependencies(`${options.cwd}`, PROJECT_DEPENDENCIES)
}

/**
 * Prompts the user for project details:
 * - Project name (validated to be less than 128 characters).
 * - Whether to use a `/src` directory.
 *
 * @returns {Promise<{ projectName: string, useSrcDir: boolean }>} - User-provided project details.
 */
const promptForNewProject = async () => {
  const { projectName, useSrcDir } = await prompts([
    {
      type: 'text',
      name: 'projectName',
      message: `What would you like to name your project?`,
      initial: 'my-app',
      format: (value: string) => value.trim(),
      validate: (value: string) => (value.length > 128 ? `Name should be less than 128 characters.` : true),
    },
    {
      type: 'confirm',
      name: 'useSrcDir',
      message: `Would you like to use /src directory?`,
      initial: true,
    },
  ])

  return {
    projectName,
    useSrcDir,
  }
}

/**
 * Creates a new Next.js project with Tailwind, ESLint, and TypeScript.
 * - Prompts for project details.
 * - Checks write permissions and prevents overwriting existing projects.
 * - Runs `create-next-app` with necessary configurations.
 * - Writes `components.json` for project settings.
 * - Sets up utilities, Tailwind config, and global styles.
 * - Installs required dependencies.
 */
const createNewProject = async (options: InitOptions) => {
  try {
    const { projectName, useSrcDir } = await promptForNewProject()

    const projectPath = path.join(options.cwd, projectName)

    try {
      await fs.access(options.cwd, fs.constants.W_OK)
    } catch (error) {
      logger.break()
      logger.error(`The path ${txt.info(options.cwd)} is not writable.`)
      logger.error(
        `Possible reasons: \n1. You do not have write permissions for this folder. \n2. The path does not exist.`
      )
      logger.break()
      process.exit(1)
    }

    //Check if the project with the same name is already created
    if (fs.existsSync(projectPath)) {
      throw new Error(`A project with the name ${projectName} already exists in the current directory.`)
    }

    //Create the Next.js project
    const createProjectSpinner = spinner(
      `Creating a new ${txt.bold(txt.info('Next.js'))} project. This might take some time.`
    ).start()
    await execa(
      'npx',
      [
        'create-next-app',
        '--tailwind',
        '--eslint',
        '--typescript',
        '--app',
        projectName,
        '--use-npm',
        useSrcDir ? '--src-dir' : '--no-src-dir',
        '--yes',
      ],
      { cwd: options.cwd, stdio: 'ignore' }
    )
    createProjectSpinner.succeed()

    //Write components.json file
    const componentSpinner = spinner('Writing components.json file').start()
    const targetPath = path.resolve(projectPath, 'components.json')
    await fs.writeFile(targetPath, JSON.stringify({ hasSrcDir: useSrcDir }, null, 2), 'utf8')
    componentSpinner.succeed()

    // Configure project
    const configurationSpinner = spinner('Setting up project configuration').start()
    await createUtils(projectPath, useSrcDir)
    // await updateTailwindConfig(projectPath)
    await createGlobalCssFile(projectPath, useSrcDir)
    configurationSpinner.succeed()

    //Install the required dependencies
    await installDependencies(`${projectPath}`, PROJECT_DEPENDENCIES)

    return {
      projectPath,
      projectName,
    }
  } catch (error) {
    handleError(error)
  }
}
